import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { TARGET_TYPES } from '../../../constants/promotionConfig';
import { getProducts } from '../../../services/products';

const imageBase = process.env.REACT_APP_IMAGES_API_URL || '';

const SelectedRow = ({ title, subtitle, imageUrl, onRemove }) => (
    <Paper
        elevation={0}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            marginBottom: 8,
            border: '1px solid #E8E8E8',
            borderRadius: 8,
            background: '#fff',
        }}
    >
        <Box
            style={{
                width: 48,
                height: 48,
                borderRadius: 6,
                background: '#EEF2F5',
                flexShrink: 0,
                backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        />
        <Box flex={1} minWidth={0}>
            <Typography variant="body2" style={{ fontWeight: 600 }} noWrap>
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="caption" color="textSecondary" display="block" noWrap>
                    {subtitle}
                </Typography>
            )}
        </Box>
        <IconButton size="small" onClick={onRemove} aria-label="Quitar">
            <CloseIcon fontSize="small" />
        </IconButton>
    </Paper>
);

const PromotionScopeSection = ({
    storeId,
    targetType,
    onTargetTypeChange,
    selectedCollections,
    selectedProducts,
    onAddCollection,
    onRemoveCollection,
    onAddProduct,
    onRemoveProduct,
    collectionsCatalog,
    isEdit,
    isTierEdit,
    loadedProductCount,
    resolvingProductNames,
    targetTypeOptions,
    storeScopeHint,
    compact,
}) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [productResults, setProductResults] = useState([]);
    const [searching, setSearching] = useState(false);

    const scopeOptions = targetTypeOptions || TARGET_TYPES;
    const isStoreScope = targetType === 'store';
    const isCollection = targetType === 'collection';
    const selectedLabel = isCollection ? 'Categorías seleccionadas' : 'Productos seleccionados';
    const addLabel = isCollection ? 'Agregar categorías' : 'Agregar productos';

    const selectedCollectionIds = useMemo(
        () => new Set(selectedCollections.map((c) => String(c.id))),
        [selectedCollections]
    );

    const displayCollections = useMemo(() => {
        const q = search.trim().toLowerCase();
        return collectionsCatalog
            .filter((c) => !q || (c.name || '').toLowerCase().includes(q))
            .map((col) => ({
                ...col,
                selected: selectedCollectionIds.has(String(col.id)),
            }));
    }, [collectionsCatalog, search, selectedCollectionIds]);

    const toggleCollection = (col) => {
        const id = String(col.id);
        if (selectedCollectionIds.has(id)) {
            onRemoveCollection(col.id);
        } else {
            onAddCollection(col);
        }
    };

    const openPicker = () => {
        setSearch('');
        setProductResults([]);
        setPickerOpen(true);
    };

    const handleSearchProducts = (term) => {
        setSearch(term);
        if (term.trim().length < 3) {
            setProductResults([]);
            return;
        }
        setSearching(true);
        getProducts(storeId, term.trim())
            .then((res) => setProductResults(Array.isArray(res.data) ? res.data : []))
            .catch(() => setProductResults([]))
            .finally(() => setSearching(false));
    };

    const collectionImage = (col) => {
        if (!col?.picture_url) return null;
        const url = col.picture_url;
        return url.startsWith('http') ? url : `${imageBase}${url}`;
    };

    const productImage = (product) => {
        const pic = product?.picture_url || product?.images?.[0]?.url;
        if (!pic) return null;
        return pic.startsWith('http') ? pic : `${imageBase}${pic}`;
    };

    return (
        <>
            <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
                <InputLabel id="promo-target-type-label">Aplica a</InputLabel>
                <Select
                    labelId="promo-target-type-label"
                    label="Aplica a"
                    value={targetType}
                    onChange={(e) => onTargetTypeChange(e.target.value)}
                    disabled={isTierEdit}
                >
                    {scopeOptions.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                            {t.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: 12, lineHeight: compact ? 1.45 : 1.5 }}
            >
                {isStoreScope
                    ? storeScopeHint ||
                      (compact
                          ? 'Aplica a toda la tienda.'
                          : 'La regla aplica a todos los productos de la tienda; no hace falta elegir categorías ni productos.')
                    : isCollection
                      ? compact
                          ? 'Aplica a los productos de las categorías elegidas.'
                          : 'El descuento se aplicará a todos los productos de las categorías que elijas.'
                      : compact
                        ? 'Aplica solo a los productos que agregues.'
                        : 'El descuento se aplicará solo a los productos que agregues aquí.'}
                {isEdit && loadedProductCount > 0 && !compact && (
                    <>
                        {' '}
                        Si no cambias la selección, al guardar se mantienen los{' '}
                        {loadedProductCount} producto(s) actuales.
                    </>
                )}
            </Typography>

            {!isStoreScope && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {selectedLabel}
                    </Typography>
                    <Button color="primary" size="small" onClick={openPicker}>
                        {addLabel}
                    </Button>
                </Box>
            )}

            {isCollection && !isStoreScope && selectedCollections.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                    Aún no has agregado categorías.
                </Typography>
            )}
            {!isCollection && !isStoreScope && selectedProducts.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                    Aún no has agregado productos.
                </Typography>
            )}

            {!isCollection && !isStoreScope && resolvingProductNames && (
                <Typography variant="body2" color="textSecondary">
                    Cargando nombres de productos…
                </Typography>
            )}

            {isCollection &&
                !isStoreScope &&
                selectedCollections.map((col) => (
                    <SelectedRow
                        key={col.id}
                        title={col.name}
                        subtitle="Categoría"
                        imageUrl={collectionImage(col)}
                        onRemove={() => onRemoveCollection(col.id)}
                    />
                ))}

            {!isCollection &&
                !isStoreScope &&
                selectedProducts.map((product) => (
                    <SelectedRow
                        key={product.id}
                        title={
                            product.name?.trim() ||
                            (resolvingProductNames ? 'Cargando…' : 'Producto sin nombre')
                        }
                        subtitle={product.sku ? `SKU: ${product.sku}` : undefined}
                        imageUrl={productImage(product)}
                        onRemove={() => onRemoveProduct(product.id)}
                    />
                ))}

            <Dialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                fullWidth
                maxWidth="sm"
                fullScreen={compact}
            >
                <DialogTitle>
                    {isCollection ? 'Agregar categorías' : 'Agregar productos'}
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder={
                            isCollection
                                ? 'Buscar categoría'
                                : 'Buscar producto (mín. 3 caracteres)'
                        }
                        value={search}
                        onChange={(e) =>
                            isCollection
                                ? setSearch(e.target.value)
                                : handleSearchProducts(e.target.value)
                        }
                        style={{ marginBottom: 12 }}
                    />
                    {isCollection ? (
                        <>
                            {selectedCollections.length > 0 && (
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    display="block"
                                    style={{ marginBottom: 8 }}
                                >
                                    {selectedCollections.length} categoría(s) seleccionada(s). Marca o
                                    desmarca para cambiar la selección.
                                </Typography>
                            )}
                            <List disablePadding>
                                {displayCollections.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">
                                        No hay categorías que coincidan con la búsqueda
                                    </Typography>
                                )}
                                {displayCollections.map((col) => (
                                    <ListItem
                                        button
                                        key={col.id}
                                        onClick={() => toggleCollection(col)}
                                        style={{ borderRadius: 8, marginBottom: 4 }}
                                    >
                                        <ListItemIcon style={{ minWidth: 40 }}>
                                            <Checkbox
                                                edge="start"
                                                checked={col.selected}
                                                tabIndex={-1}
                                                disableRipple
                                                color="primary"
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={col.name} />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    ) : (
                        <>
                            {search.trim().length < 3 && (
                                <Typography variant="body2" color="textSecondary">
                                    Escribe al menos 3 caracteres para buscar
                                </Typography>
                            )}
                            {searching && (
                                <Typography variant="body2" color="textSecondary">
                                    Buscando…
                                </Typography>
                            )}
                            <List disablePadding>
                                {productResults.map((product) => (
                                    <ListItem
                                        button
                                        key={product.id}
                                        onClick={() => {
                                            onAddProduct(product);
                                            setPickerOpen(false);
                                        }}
                                        style={{ borderRadius: 8, marginBottom: 4 }}
                                    >
                                        <ListItemText
                                            primary={product.name}
                                            secondary={product.sku ? `SKU: ${product.sku}` : null}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {isCollection && selectedCollections.length > 0 && (
                        <Typography variant="body2" color="textSecondary" style={{ flex: 1, marginLeft: 8 }}>
                            {selectedCollections.length} seleccionada(s)
                        </Typography>
                    )}
                    <Button color="primary" onClick={() => setPickerOpen(false)}>
                        Listo
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PromotionScopeSection;
